import PropTypes from 'prop-types';
import { usePermission } from '@hooks';

/**
 * Componente que renderiza children solo si el usuario tiene los permisos requeridos
 *
 * @example
 * // Permiso único
 * <PermissionGate permission="proyectos.crear">
 *   <Button>Crear Proyecto</Button>
 * </PermissionGate>
 *
 * @example
 * // Múltiples permisos (OR por defecto)
 * <PermissionGate permissions={['tareas.editar', 'tareas.eliminar']}>
 *   <EditButton />
 * </PermissionGate>
 *
 * @example
 * // Múltiples permisos (AND)
 * <PermissionGate permissions={['proyectos.editar', 'equipo.gestionar_roles']} requireAll>
 *   <AdminPanel />
 * </PermissionGate>
 */
export function PermissionGate({
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  children,
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();

  const allPermissions = permission ? [permission, ...permissions] : permissions;

  // Si no se especifican permisos, mostrar children
  if (allPermissions.length === 0) {
    return children;
  }

  const hasAccess = requireAll
    ? hasAllPermissions(allPermissions)
    : hasAnyPermission(allPermissions);

  if (!hasAccess) {
    return fallback;
  }

  return children;
}

PermissionGate.propTypes = {
  /** Permiso único requerido */
  permission: PropTypes.string,
  /** Lista de permisos (combinados con permission si se proporciona) */
  permissions: PropTypes.arrayOf(PropTypes.string),
  /** Si es true, requiere TODOS los permisos. Si es false (default), requiere AL MENOS UNO */
  requireAll: PropTypes.bool,
  /** Contenido a mostrar si no tiene permisos */
  fallback: PropTypes.node,
  /** Contenido a mostrar si tiene permisos */
  children: PropTypes.node.isRequired,
};

export default PermissionGate;
